import { apiClient } from '@/services/api'

export interface FeatureGroup {
  group_hash: string
  type_class: string | null
  subtype_class: string | null
  charge_norm: string | null
  disp_class: string | null
  disposition_norm: string | null
  offense_age_lte_7: boolean | null
  age_category: string | null
  adjudication_category: string | null
  crime_category: string | null
  court_type: string | null
  jurisdiction_state: string | null
  exclusion_applied: boolean | null
  offense_count: number
  gt_reportable: number
  gt_not_reportable: number
  gt_missing: number
  ce_reportable: number
  ce_not_reportable: number
  ce_missing: number
  matches: number
  mismatches: number
  accuracy: number | null
  avg_confidence: number | null
  min_confidence: number | null
  dominant_queue: string | null
  // Consistency
  distinct_decisions: number
  distinct_rules_patterns: number
  is_consistent: boolean
  dominant_decision: string | null
  dominant_rules: string | null
  can_approve: boolean
  // Internal review
  review_status: string
  qa_verdict: string | null
  qa_expected_decision: string | null
  qa_notes: string | null
  rule_change_suggestion: string | null
  review_priority: string | null
  reviewed_by_email: string | null
  reviewed_at: string | null
  offense_count_at_review: number | null
  accuracy_at_review: number | null
  is_consistent_at_review: boolean | null
  needs_re_review: boolean
  // External review
  external_review_status: string | null
  external_qa_verdict: string | null
  external_qa_notes: string | null
  external_reviewed_by_user_id: string | null
  external_reviewed_by_email: string | null
  external_reviewed_by_name: string | null
  external_reviewed_at: string | null
  // Flag lifecycle
  flag_status: string | null
  flag_notes: string | null
  flagged_at: string | null
  acknowledged_at: string | null
  fixed_at: string | null
}

export interface SampleOffense {
  group_hash: string
  offense_id: string
  search_id: string
  record_id: string | null
  bronze_id: string | null
  charge: string | null
  disposition: string | null
  offense_date: string | null
  offense_age: number | null
  type_class: string | null
  subtype_class: string | null
  charge_norm: string | null
  disp_class: string | null
  disposition_norm: string | null
  adjudication_category: string | null
  crime_category: string | null
  court_type: string | null
  age_category: string | null
  jurisdiction_state: string | null
  exclusion_applied: boolean | null
  ce_decision: string | null
  ce_confidence: number | null
  ce_rules_applied: string | null
  ce_rationale: string | null
  ground_truth: string | null
  match_status: string
  label_notes: string | null
  label_source: string | null
  duplicate: boolean | null
  charge_description: string | null
  statute: string | null
  plea: string | null
  arrest_date: string | null
  disposition_date: string | null
  additional_info: string | null
  sentence_info: string | null
  offense_status: string | null
  offense_type: string | null
  age_at_offense: string | null
  estimated_release_date: string | null
  effective_release_date: string | null
  sentence_incarceration_date: string | null
  sentence_release_date: string | null
  sentence_is_serving: boolean | null
  sentence_probation: string | null
  sentence_jail_time: string | null
  sentence_prison_time: string | null
  sentence_fines: string | null
  sentence_court_costs: string | null
  case_number: string | null
  jurisdiction: string | null
  file_date: string | null
}

export interface ReviewStats {
  pending_review: number
  approved: number
  flagged: number
  needs_update: number
  in_progress: number
  needs_re_review: number
  inconsistent: number
  exclusion_applied_count: number
  flags_open: number
  flags_acknowledged: number
  pending_external: number
  externally_confirmed: number
  total: number
}

export interface FilterOptions {
  type_classes: string[]
  disp_classes: string[]
  jurisdiction_states: string[]
  crime_categories: string[]
  review_statuses: string[]
  queues: string[]
  decisions: string[]
  flag_statuses: string[]
  external_review_statuses: string[]
}

export interface SubmitReviewPayload {
  qa_verdict?: string
  qa_expected_decision?: string
  qa_notes?: string
  rule_change_suggestion?: string
  review_status?: 'APPROVED' | 'FLAGGED' | 'NEEDS_UPDATE'
  priority?: string
}

export interface SubmitExternalReviewPayload {
  external_qa_verdict: 'AGREE' | 'DISAGREE' | 'NEEDS_CHANGES'
  external_qa_notes?: string
  external_review_status: 'CONFIRMED' | 'DISPUTED' | 'FLAGGED_FOR_REREVIEW'
}

export interface UpdateFlagPayload {
  flag_status: 'FLAGGED' | 'ACKNOWLEDGED' | 'FIXED'
  flag_notes?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface GroundTruthSample {
  group_hash: string
  offense_id: string
  sample_index: number
  expected_decision: string | null
  expected_rules: string | null
  confirmed_by_email: string | null
  confirmed_at: string | null
  active: boolean
}

export interface RegressionRun {
  run_id: string
  code_version: string | null
  engine_version: string | null
  triggered_by: string | null
  run_at: string
  completed_at: string | null
  total_tests: number | null
  passed: number | null
  failed: number | null
  status: string
}

export interface RegressionResult {
  run_id: string
  offense_id: string
  group_hash: string | null
  expected_decision: string | null
  actual_decision: string | null
  expected_rules: string | null
  actual_rules: string | null
  result: string
  diff_details: string | null
}

export function useFeatureGroupApi() {
  const getGroups = async (params?: Record<string, any>): Promise<PaginatedResponse<FeatureGroup>> => {
    return apiClient.get('/feature-groups', params)
  }

  const getReviewStats = async (): Promise<ReviewStats> => {
    return apiClient.get('/feature-groups/stats')
  }

  const getFilterOptions = async (): Promise<FilterOptions> => {
    return apiClient.get('/feature-groups/filters')
  }

  const getGroupById = async (id: string): Promise<FeatureGroup> => {
    return apiClient.get(`/feature-groups/${id}`)
  }

  const getSampleOffenses = async (id: string, limit = 10): Promise<SampleOffense[]> => {
    return apiClient.get(`/feature-groups/${id}/offenses`, { limit })
  }

  const submitReview = async (id: string, data: SubmitReviewPayload) => {
    return apiClient.put(`/feature-groups/${id}/review`, data)
  }

  const submitExternalReview = async (id: string, data: SubmitExternalReviewPayload) => {
    return apiClient.put(`/feature-groups/${id}/external-review`, data)
  }

  const updateFlag = async (id: string, data: UpdateFlagPayload) => {
    return apiClient.put(`/feature-groups/${id}/flag`, data)
  }

  const getGroundTruth = async (id: string): Promise<GroundTruthSample[]> => {
    return apiClient.get(`/feature-groups/${id}/ground-truth`)
  }

  const getRegressionRuns = async (limit = 20): Promise<RegressionRun[]> => {
    return apiClient.get('/feature-groups/regression/runs', { limit })
  }

  const getRegressionRunById = async (runId: string): Promise<{ run: RegressionRun, results: RegressionResult[] }> => {
    return apiClient.get(`/feature-groups/regression/runs/${runId}`)
  }

  return {
    getGroups,
    getReviewStats,
    getFilterOptions,
    getGroupById,
    getSampleOffenses,
    submitReview,
    submitExternalReview,
    updateFlag,
    getGroundTruth,
    getRegressionRuns,
    getRegressionRunById,
  }
}
